import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IMovementType, MovementTypeVisibility } from "../models/movementType";
import { IMovementTypeApiResponse } from "../models/api/movementTypeApiResponse";
import { IMovementTypeFilter } from "../models/filters/movementTypeFilter";
import MovementTypeService from "../services/MovementTypeService";
import { IMovementTypeForm } from "../models/forms/movementTypeForm";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import i18n from "@/i18n";

export const useMovementTypeStore = defineStore("movementsType", () => {
    // State
    const datas = ref<IMovementType[]>([]);
    const current = ref<IMovementType>({} as IMovementType);
    const itemForm = ref<IMovementTypeForm>({} as IMovementTypeForm);
    const isLoading = ref<boolean>(false);

    // Getters
    const semenceDatas  = computed(() => datas.value.filter(m => m.visibility.toString() === MovementTypeVisibility[MovementTypeVisibility.VisibleSemence]  || m.visibility.toString() === MovementTypeVisibility[MovementTypeVisibility.AllVisible]));
    const materielDatas = computed(() => datas.value.filter(m => m.visibility.toString() === MovementTypeVisibility[MovementTypeVisibility.VisibleMateriel] || m.visibility.toString() === MovementTypeVisibility[MovementTypeVisibility.AllVisible]));

    // Actions - Hydration
    async function hydrateMovementTypes(items: IMovementTypeApiResponse[]): Promise<IMovementType[]> {
        if (items.length === 0) return [];
        return items.map(item => ({ ...item } as unknown as IMovementType));
    }

    // Actions
    async function load(filter: IMovementTypeFilter) {
        isLoading.value = true;
        let result = await MovementTypeService.getAll<IMovementTypeFilter, IMovementTypeApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateMovementTypes(result.data as IMovementTypeApiResponse[]);
        }
        return result;
    }

    async function get(id: number) {
        let result = await MovementTypeService.getById<IMovementTypeApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateMovementTypes([result.data as IMovementTypeApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IMovementType | IDataOperationResult<IMovementType>>;
        if (itemForm.value.id > 0) {
            result = await MovementTypeService.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateMovementTypes([result.data as IMovementTypeApiResponse]);
                current.value = modelData;
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IMovementType>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await MovementTypeService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateMovementTypes([result.data as IMovementTypeApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function deleteAction() {
        let result = await MovementTypeService.delete<IMovementTypeApiResponse>(current.value.id);
        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }
            current.value = newItem();
            itemForm.value = newForm();
        }
        return result;
    }

    function toForm(item: IMovementType): IMovementTypeForm {
        const form = {} as IMovementTypeForm;
        form.id = item.id;
        form.description = item.description;
        form.name = item.name;
        form.way = item.way;
        form.visibility = item.visibility;
        form.visibleInHistory = item.visibleInHistory;
        return form;
    }

    function newForm(): IMovementTypeForm {
        itemForm.value = {
            id: 0,
            description: "",
            name: "",
            way: 0,
            visibility: MovementTypeVisibility.NotVisible,
            visibleInHistory: true
        } as IMovementTypeForm;
        return itemForm.value;
    }

    function newItem(): IMovementType {
        current.value = {
            id: 0,
            description: "",
            name: "",
            way: 0,
            visibility: MovementTypeVisibility.NotVisible,
            visibleInHistory: true
        } as IMovementType;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Getters
        semenceDatas,
        materielDatas,
        // Actions
        hydrateMovementTypes,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajMovementType", async (payload: IPayloadStore) => {
    const store = useMovementTypeStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateMovementTypes([payload.data as IMovementTypeApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateMovementTypes([payload.data as IMovementTypeApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
